import { PaymentBank, PaymentInitResponse } from '@interface/payment';
import { PromoCodeResponse, Wallet } from '@interface/wallet';
import { INITIAL_STATE } from '@utils/constants';
import { useCartStore } from 'app/(dashboard)/dashboard/(pages)/budgets/stores/userCartStore';
import { INITIAL_FILTERS } from 'app/(web)/tratamientos/utils/filters';
import { Appointment, User, UserCheckin } from 'app/types/appointment';
import { Post } from 'app/types/blog';
import { AnalyticsMetrics, Client } from 'app/types/client';
import { Clinic } from 'app/types/clinic';
import { ProductFilters } from 'app/types/filters';
import { PackUnitiesScheduled, Product } from 'app/types/product';
import { Promo } from 'app/types/promo';
import { Slot } from 'app/types/slot';
import { Dayjs } from 'dayjs';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

type DeviceSize = {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isWideScreen: boolean;
};

export enum TypeOfPayment {
  Free,
  Reservation,
  Full,
  InClinic,
}

export enum ApplicationOrigin {
  Web,
  Landing,
  Dashboard,
  Gloxbox,
  LandingAesthetic,
  LandingSurgery,
}

interface SessionStore {
  analyticsMetrics: AnalyticsMetrics;
  client: Client | undefined;
  isMobile: boolean;
  deviceSize: DeviceSize;
  selectedTreatments: Product[];
  selectedPack?: Product;
  previousSelectedTreatments: Product[];
  selectedPacksTreatments?: Product[];
  selectedClinic?: Clinic;
  selectedSlot?: Slot;
  selectedDay: Dayjs | undefined;
  previousAppointment: Appointment | undefined;
  payment: PaymentInitResponse | undefined;
  typeOfPayment: TypeOfPayment;
  appointmentUrl: string;
  dermaPhone: string;
  treatmentPacks: PackUnitiesScheduled[];
  hasSeenDashboardProfessionals: boolean;
  isCallMeTriggered: boolean;
  promoCode: PromoCodeResponse | undefined;
  walletClient: Wallet | undefined;
  giftCardPopUpVisibility: boolean;
  storedBudgetId: string | '';
  currentOrigin: ApplicationOrigin;
  appointmentComment: string;
}
interface SessionActions {
  setAnalyticsMetrics: (analyticsMetrics: AnalyticsMetrics) => void;
  setClient: (client: Client) => void;
  setIsMobile: (value: boolean) => void;
  setDeviceSize: (value: DeviceSize) => void;
  setSelectedTreatments: (value: Product[]) => void;
  setSelectedPack: (value: Product | undefined) => void;
  setPreviousSelectedTreatments: (value: Product[]) => void;
  setSelectedClinic: (value?: Clinic) => void;
  setSelectedSlot: (slot?: Slot) => void;
  setSelectedDay: (day?: Dayjs) => void;
  setPreviousAppointment: (appointment: Appointment | undefined) => void;
  setPayment: (payment: PaymentInitResponse | undefined) => void;
  setTypeOfPayment: (typeOfPayment: TypeOfPayment) => void;
  setAppointmentUrl: (url: string) => void;
  setDermaPhone: (phone: string) => void;
  setTreatmentPacks: (treatment: PackUnitiesScheduled[]) => void;
  setHasSeenDashboardProfessionals: (value: boolean) => void;
  setIsCallMeTriggered: (value: boolean) => void;
  setPromoCode: (value?: PromoCodeResponse) => void;
  setWalletClient: (value?: Wallet) => void;
  setGiftCardPopUpVisibility: (value: boolean) => void;
  setBudgetId: (value?: string) => void;
  cleanCheckoutSession: (disableClient?: boolean) => void;
  setCurrentOrigin: (value: ApplicationOrigin) => void;
  setAppointmentComment: (comment: string) => void;
}

interface GlobalPersistStore {
  stateProducts: Product[];
  dermaProducts: Product[];
  dashboardProducts: Product[];
  clinics: Clinic[];
  user?: User;
  promo: Promo | undefined;
  blogPosts: Post[] | undefined;
  remoteControl: boolean;
  ignoreMessages: boolean;
  storedBoxId: string | '';
  storedAppointmentId: string | '';
  userCheckin?: UserCheckin;
  checkSimulator: boolean;
  activePayment: PaymentBank;
  isCallCenter: boolean;
  extraInfo: boolean;
}

interface GlobalPersistActions {
  setStateProducts: (value: Product[]) => void;
  setDermaProducts: (value: Product[]) => void;
  setDashboardProducts: (value: Product[]) => void;
  setClinics: (value: Clinic[]) => void;
  setCurrentUser: (value?: User) => void;
  setPromos: (value: Promo) => void;
  setBlogPosts: (value: Post[]) => void;
  setRemoteControl: (remoteControl: boolean) => void;
  setIgnoreMessages: (ignoreMessages: boolean) => void;
  setBoxId: (setBoxId: string) => void;
  setAppointmentId: (storedAppointmentId: string) => void;
  setUserCheckIn: (value?: UserCheckin) => void;
  setCheckSimulator: (value?: boolean) => void;
  setActivePayment: (value?: PaymentBank) => void;
  setIsCallCenter: (value?: boolean) => void;
  setExtraInfo: (value?: boolean) => void;
}

export const useSessionStore = create(
  persist<SessionStore & SessionActions>(
    set => ({
      analyticsMetrics: {
        device: 0,
        locPhysicalMs: '',
        utmAdgroup: '',
        utmCampaign: '',
        utmContent: '',
        utmMedium: '',
        utmSource: '',
        utmTerm: '',
        treatmentText: '',
        externalReference: '',
        interestedTreatment: '',
        treatmentPrice: 0,
        conversionUrl: '',
        utmCity: '',
      },
      deviceSize: {
        isMobile: true,
        isTablet: false,
        isDesktop: false,
        isWideScreen: false,
      },
      client: undefined,
      selectedTreatments: [],
      selectedPack: undefined,
      previousSelectedTreatments: [],
      selectedPacksTreatments: [],
      selectedClinic: undefined,
      selectedDay: undefined,
      selectedSlot: undefined,
      previousAppointment: undefined,
      isMobile: true,
      payment: undefined,
      typeOfPayment: TypeOfPayment.Free,
      appointmentUrl: '',
      dermaPhone: '',
      treatmentPacks: [],
      hasSeenDashboardProfessionals: false,
      isCallMeTriggered: false,
      promoCode: undefined,
      walletClient: undefined,
      giftCardPopUpVisibility: true,
      currentOrigin: ApplicationOrigin.Web,
      setCurrentOrigin: value => {
        set({ currentOrigin: value });
      },
      setAppointmentUrl: value => {
        set({ appointmentUrl: value });
      },
      setClient: value => {
        set({ client: value });
      },
      setAnalyticsMetrics: value => {
        set({ analyticsMetrics: value });
      },
      setIsMobile: value => {
        set({ isMobile: value });
      },
      setDeviceSize: value => {
        set({ deviceSize: value });
      },
      setSelectedTreatments: value => {
        set({ selectedTreatments: value });
      },
      setSelectedPack: value => {
        set({ selectedPack: value });
      },
      setPreviousSelectedTreatments: value => {
        set({ previousSelectedTreatments: value });
      },
      setSelectedClinic: value => {
        set({ selectedClinic: value });
      },
      setSelectedSlot: value => {
        set({ selectedSlot: value });
      },
      setSelectedDay: value => {
        set({ selectedDay: value });
      },
      setPreviousAppointment: value => {
        set({ previousAppointment: value });
      },
      setPayment: value => {
        set({ payment: value });
      },
      setTypeOfPayment: value => {
        set({ typeOfPayment: value });
      },
      setDermaPhone: value => {
        set({ dermaPhone: value });
      },
      setTreatmentPacks: value => {
        set({ treatmentPacks: value });
      },
      setHasSeenDashboardProfessionals: value => {
        set({ hasSeenDashboardProfessionals: value });
      },
      setIsCallMeTriggered: value => {
        set({ isCallMeTriggered: value });
      },
      setPromoCode: value => {
        set({ promoCode: value });
      },
      setWalletClient: value => {
        set({ walletClient: value });
      },
      setGiftCardPopUpVisibility: value => {
        set({ giftCardPopUpVisibility: value });
      },
      storedBudgetId: '',
      setBudgetId: value => {
        set({ storedBudgetId: value });
      },
      appointmentComment: '',
      setAppointmentComment: value => {
        set({ appointmentComment: value });
      },
      cleanCheckoutSession: (disableClient = true) => {
        useCartStore.setState(INITIAL_STATE);
        const prevClientState = useSessionStore.getState().client;

        set({
          selectedClinic: undefined,
          selectedSlot: undefined,
          selectedDay: undefined,
          payment: undefined,
          client: disableClient ? undefined : prevClientState,
          previousAppointment: undefined,
          previousSelectedTreatments: [],
          selectedPack: undefined,
          selectedPacksTreatments: [],
          selectedTreatments: [],
          treatmentPacks: [],
          typeOfPayment: TypeOfPayment.Free,
        });
      },
    }),

    {
      name: 'session-storage',
      version:
        process.env.NEXT_PUBLIC_ENVIRONMENT === 'production'
          ? Number(process.env.NEXT_PUBLIC_STORE_VERSION || '0')
          : 1,
      storage: createJSONStorage(() => sessionStorage),
      migrate: persistedState => {
        const state = persistedState as SessionStore & SessionActions;
        state.selectedClinic = undefined;
        state.selectedSlot = undefined;
        state.selectedDay = undefined;
        state.payment = undefined;
        state.client = undefined;
        state.previousAppointment = undefined;
        state.selectedTreatments = [];
        state.selectedPack = undefined;
        state.previousSelectedTreatments = [];
        state.selectedPacksTreatments = [];
        state.treatmentPacks = [];
        state.typeOfPayment = TypeOfPayment.Free;
        return {
          ...state,
        };
      },
    }
  )
);

export const useGlobalPersistedStore = create(
  persist<GlobalPersistStore & GlobalPersistActions>(
    set => ({
      promo: undefined,
      blogPosts: undefined,
      stateProducts: [],
      dermaProducts: [],
      dashboardProducts: [],
      clinics: [],
      user: undefined,
      promoProduct: undefined,
      setStateProducts: (value: Product[]) => {
        set({ stateProducts: value });
      },
      setDermaProducts: (value: Product[]) => {
        set({ dermaProducts: value });
      },
      setDashboardProducts: (value: Product[]) => {
        set({ dashboardProducts: value });
      },
      setClinics: (value: Clinic[]) => {
        set({ clinics: value });
      },
      setCurrentUser: value => {
        set({ user: value });
      },
      userCheckin: undefined,
      setUserCheckIn: value => {
        set({ userCheckin: value });
      },
      setPromos: (value: Promo) => {
        set({ promo: value });
      },
      setBlogPosts: (value: Post[]) => {
        set({ blogPosts: value });
      },
      remoteControl: false,
      setRemoteControl: value => {
        set({ remoteControl: value });
      },
      ignoreMessages: false,
      setIgnoreMessages: value => {
        set({ ignoreMessages: value });
      },
      storedBoxId: '',
      setBoxId: value => {
        set({ storedBoxId: value });
      },
      storedAppointmentId: '',
      setAppointmentId: value => {
        set({ storedAppointmentId: value });
      },
      checkSimulator: false,
      setCheckSimulator: value => {
        set({ checkSimulator: value });
      },
      activePayment: PaymentBank.None,
      setActivePayment: value => {
        set({ activePayment: value });
      },
      isCallCenter: false,
      setIsCallCenter: value => {
        set({ isCallCenter: value });
      },
      extraInfo: false,
      setExtraInfo: value => {
        set({ extraInfo: value });
      },
    }),
    {
      name: 'global-storage',
      version:
        process.env.NEXT_PUBLIC_ENVIRONMENT === 'production'
          ? Number(process.env.NEXT_PUBLIC_STORE_VERSION || '0')
          : 1,
      migrate: persistedState => {
        const state = persistedState as GlobalPersistStore &
          GlobalPersistActions;
        state.promo = undefined;
        state.blogPosts = undefined;
        state.stateProducts = [];
        state.dermaProducts = [];
        state.dashboardProducts = [];
        state.clinics = [];
        state.user = undefined;
        state.dermaProducts = [];
        state.clinics = [];
        state.user = undefined;
        state.stateProducts = [];
        state.dashboardProducts = [];
        state.userCheckin = undefined;
        state.remoteControl = false;
        state.ignoreMessages = false;
        state.storedBoxId = '';
        state.storedAppointmentId = '';
        state.checkSimulator = false;
        state.activePayment = PaymentBank.None;
        state.isCallCenter = false;
        state.extraInfo = false;
        return state;
      },
    }
  )
);

interface GlobalStore {
  filteredProducts: Product[];
  productFilters: ProductFilters;
}

interface GlobalActions {
  setFilteredProducts: (value: Product[]) => void;
  setProductFilters: (value: ProductFilters) => void;
}

export const useGlobalStore = create<GlobalStore & GlobalActions>(set => ({
  filteredProducts: [],
  productFilters: INITIAL_FILTERS,
  setFilteredProducts: (value: Product[]) => {
    set({ filteredProducts: value });
  },
  setProductFilters: (value: ProductFilters) => {
    set({ productFilters: value });
  },
}));
